import { useCallback } from "react"

const AUTH_REDIRECT_CONTEXT_KEY = "auth-redirect-context"
const LAST_AUTH_ADDRESS_KEY = "last-auth-address"

export interface RedirectFromState {
  pathname: string
  search?: string
  hash?: string
}

export interface AuthRedirectContext {
  from: RedirectFromState
  previousAddress: string | null
}

const normalizeAddress = (value?: string | null): string | null => {
  if (!value) return null
  return value.toLowerCase()
}

const isBrowser = () => typeof window !== "undefined"

const parseRedirectContext = (state: unknown): AuthRedirectContext | null => {
  if (!state || typeof state !== "object") return null

  const s = state as Record<string, unknown>
  const rawFrom = s.from

  if (!rawFrom || typeof rawFrom !== "object") return null

  const fromObj = rawFrom as Record<string, unknown>
  const pathname = typeof fromObj.pathname === "string" ? fromObj.pathname : ""

  if (!pathname) return null

  const search = typeof fromObj.search === "string" ? fromObj.search : ""
  const hash = typeof fromObj.hash === "string" ? fromObj.hash : ""

  const previousAddress =
    typeof s.previousAddress === "string"
      ? normalizeAddress(s.previousAddress)
      : s.previousAddress === null
        ? null
        : null

  return {
    from: {
      pathname,
      search,
      hash,
    },
    previousAddress,
  }
}

export const usePersistentAuthRedirect = () => {
  const getLastAuthAddress = useCallback((): string | null => {
    if (!isBrowser()) return null
    const saved = sessionStorage.getItem(LAST_AUTH_ADDRESS_KEY)
    return normalizeAddress(saved)
  }, [])

  const markLastAuthAddress = useCallback((address?: string | null) => {
    if (!isBrowser()) return

    const normalizedAddress = normalizeAddress(address)
    if (!normalizedAddress) return

    sessionStorage.setItem(LAST_AUTH_ADDRESS_KEY, normalizedAddress)
  }, [])

  const saveRedirectContext = useCallback(
    (params: { from: RedirectFromState; previousAddress?: string | null }) => {
      if (!isBrowser()) return

      const previousAddress =
        normalizeAddress(params.previousAddress) ?? getLastAuthAddress()

      const context: AuthRedirectContext = {
        from: params.from,
        previousAddress,
      }

      sessionStorage.setItem(AUTH_REDIRECT_CONTEXT_KEY, JSON.stringify(context))
    },
    [getLastAuthAddress]
  )

  const getRedirectContext = useCallback((state?: unknown) => {
    const fromState = parseRedirectContext(state)
    if (fromState) return fromState

    if (!isBrowser()) return null

    const raw = sessionStorage.getItem(AUTH_REDIRECT_CONTEXT_KEY)
    if (!raw) return null

    try {
      return parseRedirectContext(JSON.parse(raw) as unknown)
    } catch {
      return null
    }
  }, [])

  const clearRedirectContext = useCallback(() => {
    if (!isBrowser()) return
    sessionStorage.removeItem(AUTH_REDIRECT_CONTEXT_KEY)
  }, [])

  const resolvePostLoginPath = useCallback(
    (params: { currentAddress?: string | null; state?: unknown }) => {
      const context = getRedirectContext(params.state)
      const currentAddress = normalizeAddress(params.currentAddress)
      const previousAddress = context?.previousAddress ?? null

      const isFirstLogin = !previousAddress
      const isSameAccount =
        !!currentAddress && currentAddress === previousAddress

      const fromPathname = context?.from.pathname ?? ""
      const fromSearch = context?.from.search ?? ""
      const fromHash = context?.from.hash ?? ""

      const shouldReturnToProtectedPage =
        !!fromPathname &&
        fromPathname !== "/auth" &&
        (isFirstLogin || isSameAccount)

      const nextPath = shouldReturnToProtectedPage
        ? `${fromPathname}${fromSearch}${fromHash}`
        : "/"

      clearRedirectContext()
      if (currentAddress) {
        markLastAuthAddress(currentAddress)
      }

      return nextPath
    },
    [clearRedirectContext, getRedirectContext, markLastAuthAddress]
  )

  return {
    markLastAuthAddress,
    saveRedirectContext,
    getRedirectContext,
    clearRedirectContext,
    resolvePostLoginPath,
  }
}
